import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

import type {
  AdminConsentTypeInput,
  AdminConsentTypeViewModel,
  UpdateAdminConsentTypeInput,
} from '../api';

interface DraftState {
  name: string;
  description: string;
  isRequired: boolean;
  isActive: boolean;
}

const EMPTY_DRAFT: DraftState = {
  name: '',
  description: '',
  isRequired: false,
  isActive: true,
};

function fromRow(row: AdminConsentTypeViewModel): DraftState {
  return {
    name: row.name,
    description: row.description,
    isRequired: row.isRequired,
    isActive: row.isActive,
  };
}

interface FieldErrors {
  name?: string;
}

/**
 * Single dialog used for both create and edit — the row prop drives the mode.
 * When `row === null` we render the create title/submit label; otherwise edit
 * mode fills the form with the current values and diffs against them so the
 * PATCH only touches changed fields.
 */
export interface ConsentTypeDialogProps {
  readonly open: boolean;
  readonly row: AdminConsentTypeViewModel | null;
  readonly onCancel: () => void;
  readonly onCreate: (input: AdminConsentTypeInput) => void;
  readonly onUpdate: (input: UpdateAdminConsentTypeInput) => void;
  readonly pending: boolean;
  readonly submitError: unknown;
}

export function ConsentTypeDialog({
  open,
  row,
  onCancel,
  onCreate,
  onUpdate,
  pending,
  submitError,
}: ConsentTypeDialogProps): ReactElement {
  const { t } = useTranslation();
  const isEdit = row !== null;
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT);
  const [errors, setErrors] = useState<FieldErrors>({});

  // Reset when the dialog opens or the target row changes.
  useEffect(() => {
    if (!open) return;
    setDraft(row !== null ? fromRow(row) : EMPTY_DRAFT);
    setErrors({});
  }, [open, row]);

  function handleClose(next: boolean): void {
    if (pending) return;
    if (!next) onCancel();
  }

  function validate(): AdminConsentTypeInput | null {
    const name = draft.name.trim();
    const nextErrors: FieldErrors = {};
    if (name.length === 0) {
      nextErrors.name = t('routes.admin.consentTypes.errors.nameRequired');
    } else if (name.length > 255) {
      nextErrors.name = t('routes.admin.consentTypes.errors.nameTooLong');
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return null;
    }
    setErrors({});
    return {
      name,
      description: draft.description.trim(),
      // Position is managed via arrow reorder in the table — the page passes
      // the real sortOrder on create; edit reuses the existing value.
      sortOrder: row?.sortOrder ?? 0,
      isRequired: draft.isRequired,
      isActive: draft.isActive,
    };
  }

  function diffChanged(
    input: AdminConsentTypeInput,
    original: AdminConsentTypeViewModel,
  ): readonly (keyof AdminConsentTypeInput)[] {
    const changed: (keyof AdminConsentTypeInput)[] = [];
    if (input.name !== original.name) changed.push('name');
    if (input.description !== original.description) changed.push('description');
    if (input.isRequired !== original.isRequired) changed.push('isRequired');
    if (input.isActive !== original.isActive) changed.push('isActive');
    return changed;
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const input = validate();
    if (input === null) return;
    if (row === null) {
      onCreate(input);
    } else {
      onUpdate({ id: row.id, ...input, changed: diffChanged(input, row) });
    }
  }

  const submitErrorCode = getErrorCodeForUi(submitError);
  const submitErrorKey = isEdit
    ? 'routes.admin.consentTypes.errors.submitEdit'
    : 'routes.admin.consentTypes.errors.submitCreate';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[calc(100vh-4rem)] overflow-y-auto sm:max-w-xl" disableOutsideClose>
        <DialogHeader>
          <DialogTitle>
            {t(isEdit ? 'routes.admin.consentTypes.edit.title' : 'routes.admin.consentTypes.create.title')}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <FormField
            id="admin-consent-type-name"
            label={t('routes.admin.consentTypes.fields.name')}
            required
            error={errors.name}
          >
            {(fp) => (
              <Input
                {...fp}
                value={draft.name}
                onChange={(e) => {
                  setDraft((p) => ({ ...p, name: e.target.value }));
                }}
                autoComplete="off"
                spellCheck={false}
                placeholder={t('routes.admin.consentTypes.fields.namePlaceholder')}
              />
            )}
          </FormField>
          <FormField
            id="admin-consent-type-description"
            label={t('routes.admin.consentTypes.fields.description')}
          >
            {(fp) => (
              <Textarea
                {...fp}
                value={draft.description}
                rows={3}
                onChange={(e) => {
                  setDraft((p) => ({ ...p, description: e.target.value }));
                }}
                placeholder={t('routes.admin.consentTypes.fields.descriptionPlaceholder')}
              />
            )}
          </FormField>
          <div className="grid grid-cols-1 gap-x-4 gap-y-2 border-t border-border pt-4 sm:grid-cols-2">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.isRequired}
                onChange={(e) => {
                  setDraft((p) => ({ ...p, isRequired: e.target.checked }));
                }}
                className="h-3.5 w-3.5"
              />
              {t('routes.admin.consentTypes.fields.isRequired')}
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.isActive}
                onChange={(e) => {
                  setDraft((p) => ({ ...p, isActive: e.target.checked }));
                }}
                className="h-3.5 w-3.5"
              />
              {t('routes.admin.consentTypes.fields.isActive')}
            </label>
          </div>
          {submitError !== undefined && submitError !== null && (
            <p role="alert" className="text-sm text-red-600">
              {t(submitErrorKey, { code: submitErrorCode })}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
              {t('routes.admin.consentTypes.cancel')}
            </Button>
            <Button type="submit" loading={pending}>
              {t(isEdit ? 'routes.admin.consentTypes.edit.submit' : 'routes.admin.consentTypes.create.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
